import { supabase } from './supabase';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const PERIOD_DEFAULTS = {
  'Breakfast': { start: '06:00', end: '11:00' },
  'Lunch': { start: '11:00', end: '16:00' },
  'Dinner': { start: '16:00', end: '23:00' },
  'Late Night': { start: '22:00', end: '02:00' },
};

function getWeekDates(weekOffset = 0) {
  const now = new Date();
  const day = now.getDay();
  const diff = now.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(now);
  monday.setDate(diff + weekOffset * 7);
  monday.setHours(0, 0, 0, 0);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
}

function formatDate(d) { return d.toISOString().split('T')[0]; }

function calcHours(start, end) {
  if (!start || !end) return 7;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let h = (eh * 60 + em - sh * 60 - sm) / 60;
  if (h < 0) h += 24;
  return Math.round(h * 10) / 10;
}

function applyStagger(period, role, stagger, offsetIndex) {
  const defaultStart = PERIOD_DEFAULTS[period]?.start || '10:00';
  if (!stagger || !stagger.role_offsets || stagger.enabled === false) return defaultStart;
  if (Array.isArray(stagger.disabled_roles) && stagger.disabled_roles.includes(role)) return defaultStart;
  if (offsetIndex === 0) return defaultStart;
  const offset = stagger.role_offsets[role];
  if (offset === undefined || offset === null) return defaultStart;
  const rushTime = period === 'Lunch' ? stagger.lunch_rush_time
    : period === 'Breakfast' ? (stagger.brunch_rush_time || '09:00')
    : stagger.dinner_rush_time;
  if (!rushTime) return defaultStart;
  const [rh, rm] = rushTime.split(':').map(Number);
  const total = rh * 60 + rm + offset + (offsetIndex * 15);
  const newH = Math.floor(((total % 1440) + 1440) % 1440 / 60);
  const newM = ((total % 60) + 60) % 60;
  const [psh, psm] = defaultStart.split(':').map(Number);
  const [peh, pem] = (PERIOD_DEFAULTS[period]?.end || '23:00').split(':').map(Number);
  const staggeredMins = newH * 60 + newM;
  const periodStartMins = psh * 60 + psm;
  const periodEndMins = peh * 60 + pem;
  if (staggeredMins >= periodEndMins || staggeredMins < periodStartMins - 60) return defaultStart;
  return `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
}

/**
 * Runs schedule generation for the current week.
 * Returns { shifts, score, deductions, shiftsFilled, shiftsTotal, employeesScheduled, conflicts }
 */
export async function runScheduleGeneration(companyId, weekOffset = 0) {
  const weekDates = getWeekDates(weekOffset);
  const dateStrings = weekDates.map(formatDate);

  const [empsRes, tplRes, staffRes, staggerRes, timeOffRes, ocRes, hoursRes, companyRes] = await Promise.all([
    supabase.from('employees').select('*').eq('company_id', companyId),
    supabase.from('service_templates').select('*').eq('company_id', companyId),
    supabase.from('staffing_levels').select('*').eq('company_id', companyId),
    supabase.from('smart_stagger').select('*').eq('company_id', companyId).single(),
    supabase.from('requests').select('*').eq('company_id', companyId).eq('type', 'time_off').eq('status', 'approved'),
    supabase.from('opener_closer_settings').select('*').eq('company_id', companyId),
    supabase.from('restaurant_hours').select('*').eq('company_id', companyId),
    supabase.from('companies').select('schedule_end_codes').eq('id', companyId).single(),
  ]);
  const endCodeList = Array.isArray(companyRes?.data?.schedule_end_codes) ? companyRes.data.schedule_end_codes : [];
  const hasCL = endCodeList.some(ec => ec && ec.code === 'CL');
  const hasBD = endCodeList.some(ec => ec && ec.code === 'BD');

  const blockedTimeOff = new Set();
  (timeOffRes.data || []).forEach(r => {
    let eid = r.employee_id ? String(r.employee_id) : null;
    if (!eid && r.employee_name) {
      const match = (empsRes.data || []).find(e => e.name === r.employee_name);
      if (match) eid = String(match.id);
    }
    if (!eid) return;
    let dates = Array.isArray(r.dates) ? r.dates : [];
    if (dates.length === 0) {
      const text = (r.detail || '') + ' ' + (r.note || '');
      dates = (text.match(/\d{4}-\d{2}-\d{2}/g) || []);
    }
    dates.forEach(d => blockedTimeOff.add(`${eid}-${d}`));
  });

  const emps = empsRes.data || [];
  const freshStagger = staggerRes.data || null;

  const tpl = {};
  if (tplRes.data && tplRes.data.length > 0) {
    tplRes.data.filter(t => t.is_active).forEach(t => {
      if (!tpl[t.day_of_week]) tpl[t.day_of_week] = [];
      tpl[t.day_of_week].push(t.meal_period);
    });
  } else {
    Object.assign(tpl, { Mon: ['Lunch', 'Dinner'], Tue: ['Lunch', 'Dinner'], Wed: ['Lunch', 'Dinner'], Thu: ['Lunch', 'Dinner'], Fri: ['Lunch', 'Dinner'], Sat: ['Breakfast', 'Lunch', 'Dinner'], Sun: ['Breakfast', 'Lunch'] });
  }

  const staffLevels = {};
  (staffRes.data || []).forEach(d => { staffLevels[`${d.day_of_week}-${d.meal_period}-${d.role}`] = d.required_count; });

  // Delete existing shifts for this week
  await supabase.from('shifts').delete().eq('company_id', companyId).in('day', dateStrings);

  const toInsert = [];
  const scheduled = {};
  const empWeekHours = {};
  emps.forEach(e => { empWeekHours[String(e.id)] = 0; });

  // STEP 1: Lock in recurring shifts
  for (const emp of emps) {
    const recurring = Array.isArray(emp.recurring) ? emp.recurring : [];
    const eid = String(emp.id);
    const maxH = Math.min(emp.max_hours_per_week || 40, emp.target_hours || 40);
    for (const r of recurring) {
      if (!r.active) continue;
      const matchIdx = DAYS.indexOf(r.day);
      if (matchIdx === -1) continue;
      if (!(tpl[r.day] || []).includes(r.shift)) continue;
      const dateStr = formatDate(weekDates[matchIdx]);
      if (blockedTimeOff.has(`${eid}-${dateStr}`)) continue;
      const slotKey = `${eid}-${dateStr}-${r.shift}`;
      if (scheduled[slotKey]) continue;
      const defaultStart = PERIOD_DEFAULTS[r.shift]?.start || '10:00';
      const endTime = PERIOD_DEFAULTS[r.shift]?.end || '23:00';
      const periodHrs = calcHours(defaultStart, endTime);
      if ((empWeekHours[eid] || 0) + periodHrs > maxH) continue;
      scheduled[slotKey] = true;
      const startTime = applyStagger(r.shift, r.role, freshStagger, 0);
      empWeekHours[eid] = (empWeekHours[eid] || 0) + periodHrs;
      toInsert.push({ company_id: companyId, employee_id: emp.id, day: dateStr, shift_name: r.shift, role: r.role, start_time: startTime, end_time: endTime, is_recurring: true, is_active: true, is_opener: false, is_closer: false });
    }
  }

  // Helper: count unique days an employee is scheduled
  function empDaysScheduled(eid) {
    return new Set(toInsert.filter(s => String(s.employee_id) === String(eid)).map(s => s.day)).size;
  }
  // Helper: check if employee is already working another period on this date
  function isWorkingOtherPeriod(eid, dateStr, currentPeriod) {
    return toInsert.some(s => String(s.employee_id) === String(eid) && s.day === dateStr && s.shift_name !== currentPeriod);
  }

  // STEP 2: Fill required staffing slots
  const unfilledSlots = [];

  for (let di = 0; di < weekDates.length; di++) {
    const date = weekDates[di];
    const dayName = DAYS[di];
    const activePeriods = tpl[dayName] || [];

    for (const period of activePeriods) {
      const neededRoles = {};
      for (const [key, count] of Object.entries(staffLevels)) {
        if (count > 0 && key.startsWith(`${dayName}-${period}-`)) {
          const role = key.split('-').slice(2).join('-');
          neededRoles[role] = count;
        }
      }

      for (const [role, required] of Object.entries(neededRoles)) {
        const dateStr = formatDate(date);
        const alreadyScheduled = toInsert.filter(s => s.day === dateStr && s.shift_name === period && s.role === role).length;
        const stillNeeded = required - alreadyScheduled;
        if (stillNeeded <= 0) continue;

        const defaultStart = PERIOD_DEFAULTS[period]?.start || '10:00';
        const defaultEnd = PERIOD_DEFAULTS[period]?.end || '23:00';
        const periodHrs = calcHours(defaultStart, defaultEnd);
        const [endH] = defaultEnd.split(':').map(Number);

        const rejectionLog = [];
        const roleEmps = emps.filter(emp => {
          const roles = typeof emp.role === 'string' ? emp.role.split(',').map(r => r.trim()) : (emp.role || []);
          return roles.includes(role);
        });

        if (roleEmps.length === 0) {
          rejectionLog.push(`No employees have the ${role} role`);
        }

        const eligible = roleEmps
          .filter(emp => {
            const eid = String(emp.id);
            // Time off
            if (blockedTimeOff.has(`${eid}-${dateStr}`)) { rejectionLog.push(`${emp.name} — time off approved`); return false; }
            // Availability whitelist
            const avail = (emp.availability || '').split(',').map(d => d.trim());
            if (!avail.includes(dayName)) { rejectionLog.push(`${emp.name} — not available ${dayName}s`); return false; }
            // Unavailability blacklist (JSONB)
            const unavail = Array.isArray(emp.unavailable) ? emp.unavailable : [];
            const dayBlock = unavail.find(u => u.day === dayName);
            if (dayBlock) {
              if (dayBlock.allDay) { rejectionLog.push(`${emp.name} — unavailable ${dayName}s (all day)`); return false; }
              if (dayBlock.periods && dayBlock.periods.includes(period)) { rejectionLog.push(`${emp.name} — unavailable ${dayName} ${period}`); return false; }
            }
            // Already in this period
            const slotKey = `${eid}-${dateStr}-${period}`;
            if (scheduled[slotKey]) { rejectionLog.push(`${emp.name} — already scheduled ${period}`); return false; }
            // Max days per week
            const maxDays = emp.max_days_per_week ?? 6;
            const daysUsed = empDaysScheduled(eid);
            const alreadyOnThisDay = toInsert.some(s => String(s.employee_id) === eid && s.day === dateStr);
            if (!alreadyOnThisDay && daysUsed >= maxDays) { rejectionLog.push(`${emp.name} — at max days (${daysUsed}/${maxDays})`); return false; }
            // No clopens (respect preference)
            const noClopens = emp.no_clopens !== false; // default true
            if (noClopens) {
              if (period === 'Breakfast' && di > 0) {
                const prevDate = formatDate(weekDates[di - 1]);
                if (scheduled[`${eid}-${prevDate}-Dinner`]) { rejectionLog.push(`${emp.name} — no clopens preference`); return false; }
              }
              if (period === 'Dinner' && di < weekDates.length - 1) {
                const nextDate = formatDate(weekDates[di + 1]);
                if (scheduled[`${eid}-${nextDate}-Breakfast`]) { rejectionLog.push(`${emp.name} — no clopens preference`); return false; }
              }
            }
            // Minor restrictions
            if (emp.is_minor) {
              const minorMaxH = Math.min(emp.max_hours_per_week || 40, 20);
              if ((empWeekHours[eid] || 0) + periodHrs > minorMaxH) { rejectionLog.push(`${emp.name} — minor: max 20 hrs/week`); return false; }
              if (endH >= 22 || endH < 6) { rejectionLog.push(`${emp.name} — minor: cannot work past 10 PM`); return false; }
            }
            // Max hours
            const maxH = emp.is_minor ? Math.min(emp.max_hours_per_week || 40, 20) : (emp.max_hours_per_week || 40);
            if ((empWeekHours[eid] || 0) + periodHrs > maxH) {
              rejectionLog.push(`${emp.name} — would exceed max hours (${Math.round(empWeekHours[eid] || 0)}+${Math.round(periodHrs)}=${Math.round((empWeekHours[eid] || 0) + periodHrs)}, max ${maxH})`);
              return false;
            }
            return true;
          })
          .sort((a, b) => {
            // 1. Preferred shift matches this period
            const prefsA = (a.preferred_shifts || a.preferred_shift || '').split(',').map(s => s.trim());
            const prefsB = (b.preferred_shifts || b.preferred_shift || '').split(',').map(s => s.trim());
            const prefA = prefsA.includes(period) ? -1 : 0;
            const prefB = prefsB.includes(period) ? -1 : 0;
            if (prefA !== prefB) return prefA - prefB;
            // 2. Prefers doubles AND already working another period today → boost
            const aWorking = isWorkingOtherPeriod(a.id, dateStr, period);
            const bWorking = isWorkingOtherPeriod(b.id, dateStr, period);
            const aDoubleBoost = aWorking && a.prefers_doubles ? -1 : aWorking && !a.prefers_doubles ? 1 : 0;
            const bDoubleBoost = bWorking && b.prefers_doubles ? -1 : bWorking && !b.prefers_doubles ? 1 : 0;
            if (aDoubleBoost !== bDoubleBoost) return aDoubleBoost - bDoubleBoost;
            // 3. Furthest below target hours
            const gapA = (a.target_hours || 30) - (empWeekHours[String(a.id)] || 0);
            const gapB = (b.target_hours || 30) - (empWeekHours[String(b.id)] || 0);
            if (gapA !== gapB) return gapB - gapA;
            // 4. Fewest total hours
            const hrsA = empWeekHours[String(a.id)] || 0;
            const hrsB = empWeekHours[String(b.id)] || 0;
            if (hrsA !== hrsB) return hrsA - hrsB;
            // 5. Fewest days (spread shifts around)
            return empDaysScheduled(a.id) - empDaysScheduled(b.id);
          });

        let staggerIdx = alreadyScheduled;
        let filled = 0;
        for (let i = 0; filled < stillNeeded && i < eligible.length; i++) {
          const emp = eligible[i];
          const eid = String(emp.id);
          const maxH = emp.is_minor ? Math.min(emp.max_hours_per_week || 40, 20) : (emp.max_hours_per_week || 40);
          if ((empWeekHours[eid] || 0) + periodHrs > maxH) continue;
          const slotKey = `${eid}-${dateStr}-${period}`;
          scheduled[slotKey] = true;
          const startTime = applyStagger(period, role, freshStagger, staggerIdx);
          empWeekHours[eid] = (empWeekHours[eid] || 0) + periodHrs;
          staggerIdx++;
          filled++;
          toInsert.push({ company_id: companyId, employee_id: emp.id, day: dateStr, shift_name: period, role, start_time: startTime, end_time: defaultEnd, is_recurring: false, is_active: true, is_opener: false, is_closer: false });
        }

        if (filled < stillNeeded) {
          unfilledSlots.push({
            day: dayName, date: dateStr, period, role,
            needed: stillNeeded, filled, remaining: stillNeeded - filled,
            reasons: rejectionLog.slice(0, 5),
          });
        }
      }
    }
  }

  // ── STEP 3 & 4: Assign Openers & Closers ──
  const ocSettings = ocRes.data || [];
  const restHours = {};
  (hoursRes.data || []).forEach(h => { restHours[h.day_of_week] = h; });

  if (ocSettings.length > 0) {
    const MEAL_ORDER = ['Breakfast', 'Lunch', 'Dinner', 'Late Night'];

    for (let di = 0; di < weekDates.length; di++) {
      const dayName = DAYS[di];
      const dateStr = formatDate(weekDates[di]);
      const activePeriods = (tpl[dayName] || []).sort((a, b) => MEAL_ORDER.indexOf(a) - MEAL_ORDER.indexOf(b));
      if (activePeriods.length === 0) continue;

      const firstPeriod = activePeriods[0];
      const lastPeriod = activePeriods[activePeriods.length - 1];
      const rh = restHours[dayName];
      const restaurantOpen = rh?.open_time || PERIOD_DEFAULTS[firstPeriod]?.start || '10:00';
      const restaurantClose = rh?.close_time || PERIOD_DEFAULTS[lastPeriod]?.end || '23:00';

      const dayOcSettings = ocSettings.filter(s => s.day_of_week === dayName);

      // Track which employees are assigned as openers this day (to avoid same person O+C)
      const dayOpeners = new Set();

      for (const oc of dayOcSettings) {
        // Per-period gating: openers only come from rows matching the first period (or legacy null),
        // closers only come from rows matching the last period (or legacy null).
        const openerGate = !oc.meal_period || oc.meal_period === firstPeriod;
        const closerGate = !oc.meal_period || oc.meal_period === lastPeriod;

        // STEP 3: Assign Openers
        if (openerGate && oc.has_openers && oc.opener_count > 0) {
          const openerShifts = toInsert.filter(s => s.day === dateStr && s.shift_name === firstPeriod && s.role === oc.role);
          // Filter to can_open employees, sort by preference
          const canOpenShifts = openerShifts
            .filter(s => {
              const emp = emps.find(e => String(e.id) === String(s.employee_id));
              return !emp || emp.can_open !== false; // default true
            })
            .sort((a, b) => {
              if (a.is_recurring !== b.is_recurring) return b.is_recurring ? 1 : -1;
              return (empWeekHours[String(a.employee_id)] || 0) - (empWeekHours[String(b.employee_id)] || 0);
            });
          const openerCount = Math.min(oc.opener_count, canOpenShifts.length);
          for (let i = 0; i < openerCount; i++) {
            const [oh, om] = restaurantOpen.split(':').map(Number);
            const mins = oh * 60 + om - (oc.opener_mins_before_open || 30);
            const newH = Math.floor(((mins % 1440) + 1440) % 1440 / 60);
            const newM = ((mins % 60) + 60) % 60;
            canOpenShifts[i].start_time = `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
            canOpenShifts[i].is_opener = true;
            dayOpeners.add(String(canOpenShifts[i].employee_id));
            // Default: openers get a BD end code so they leave before close. Only applies
            // when the cell has no explicit end_mix — once a manager sets a mix, their
            // intent wins (e.g. set BD=0 to prevent auto-labeling openers).
            const cellMixEmpty = !oc.end_mix || Object.keys(oc.end_mix).length === 0;
            if (hasBD && cellMixEmpty) canOpenShifts[i].end_code = 'BD';
          }
          if (openerCount < oc.opener_count) {
            const cantOpen = openerShifts.filter(s => { const emp = emps.find(e => String(e.id) === String(s.employee_id)); return emp && emp.can_open === false; });
            unfilledSlots.push({ day: dayName, date: dateStr, period: firstPeriod, role: oc.role, needed: oc.opener_count, filled: openerCount, remaining: oc.opener_count - openerCount, reasons: cantOpen.map(s => { const emp = emps.find(e => String(e.id) === String(s.employee_id)); return `${emp?.name || '?'} — cannot open`; }).slice(0, 3) });
          }
        }

        // STEP 4: Assign Closers
        if (closerGate && oc.has_closers && oc.closer_count > 0) {
          const closerShifts = toInsert.filter(s => s.day === dateStr && s.shift_name === lastPeriod && s.role === oc.role);
          // Filter: can_close, not a minor, prefer non-openers for single-period workers
          const canCloseShifts = closerShifts
            .filter(s => {
              const emp = emps.find(e => String(e.id) === String(s.employee_id));
              if (emp && emp.can_close === false) return false;
              if (emp && emp.is_minor) return false; // minors can't close
              return true;
            })
            .sort((a, b) => {
              const aIsOpener = dayOpeners.has(String(a.employee_id));
              const bIsOpener = dayOpeners.has(String(b.employee_id));
              const aDouble = toInsert.filter(s => s.day === dateStr && String(s.employee_id) === String(a.employee_id)).length > 1;
              const bDouble = toInsert.filter(s => s.day === dateStr && String(s.employee_id) === String(b.employee_id)).length > 1;
              // Prefer non-openers (unless they're working a double — then it's fine)
              if (!aDouble && !bDouble && aIsOpener !== bIsOpener) return aIsOpener ? 1 : -1;
              if (aDouble !== bDouble) return bDouble ? 1 : -1;
              if (a.is_recurring !== b.is_recurring) return b.is_recurring ? 1 : -1;
              return 0;
            });
          const closerCount = Math.min(oc.closer_count, canCloseShifts.length);
          for (let i = 0; i < closerCount; i++) {
            const [ch, cm] = restaurantClose.split(':').map(Number);
            const mins = ch * 60 + cm + (oc.closer_mins_after_close || 30);
            const newH = Math.floor(((mins % 1440) + 1440) % 1440 / 60);
            const newM = ((mins % 60) + 60) % 60;
            canCloseShifts[i].end_time = `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
            canCloseShifts[i].is_closer = true;
            if (hasCL) canCloseShifts[i].end_code = 'CL';
          }
          if (closerCount < oc.closer_count) {
            const cantClose = closerShifts.filter(s => { const emp = emps.find(e => String(e.id) === String(s.employee_id)); return emp && (emp.can_close === false || emp.is_minor); });
            unfilledSlots.push({ day: dayName, date: dateStr, period: lastPeriod, role: oc.role, needed: oc.closer_count, filled: closerCount, remaining: oc.closer_count - closerCount, reasons: cantClose.map(s => { const emp = emps.find(e => String(e.id) === String(s.employee_id)); return `${emp?.name || '?'} — ${emp?.is_minor ? 'minor' : 'cannot close'}`; }).slice(0, 3) });
          }
        }
      }

      // STEP 5: Apply end_mix labels per (period, role) cell.
      // For each per-period OC row with an end_mix, tag that many shifts with the end code.
      // Non-closers come first so the closer's computed late end_time stays unlabeled by default —
      // the user can still override any of this by editing a shift afterward.
      for (const oc of dayOcSettings) {
        if (!oc.meal_period) continue;
        const mix = oc.end_mix && typeof oc.end_mix === 'object' ? oc.end_mix : {};
        const entries = Object.entries(mix).filter(([, n]) => Number(n) > 0);
        if (entries.length === 0) continue;
        const cell = toInsert.filter(s => s.day === dateStr && s.shift_name === oc.meal_period && s.role === oc.role);
        // Closers already got end_code='CL' (if CL exists) — skip shifts that are already coded
        // so end_mix only fills the remaining, non-closer slots.
        const unassigned = cell.filter(s => !s.end_code).sort((a, b) => (a.is_closer ? 1 : 0) - (b.is_closer ? 1 : 0));
        let idx = 0;
        for (const [code, count] of entries) {
          // If CL is in the mix but closers already got CL, subtract the number already assigned.
          const already = code === 'CL' && hasCL ? cell.filter(s => s.end_code === 'CL').length : 0;
          const remaining = Math.max(0, Number(count) - already);
          for (let i = 0; i < remaining && idx < unassigned.length; i++, idx++) {
            unassigned[idx].end_code = code;
          }
        }
      }
    }
  }

  if (toInsert.length > 0) {
    await supabase.from('shifts').insert(toInsert);
  }

  // Calculate score
  let sc = 100;
  const deductions = [];
  let hasClopen = false;
  emps.forEach(emp => {
    const empShifts = toInsert.filter(sh => String(sh.employee_id) === String(emp.id)).sort((a, b) => a.day.localeCompare(b.day));
    for (let i = 0; i < empShifts.length - 1; i++) {
      const diff = (new Date(empShifts[i + 1].day + 'T12:00:00') - new Date(empShifts[i].day + 'T12:00:00')) / 86400000;
      if (diff === 1 && parseInt(empShifts[i].end_time) >= 22 && parseInt(empShifts[i + 1].start_time) <= 8) hasClopen = true;
    }
  });
  if (hasClopen) { sc -= 20; deductions.push({ text: 'Clopen detected', pts: -20 }); }

  let hasStreak = false;
  emps.forEach(emp => {
    const days = [...new Set(toInsert.filter(sh => String(sh.employee_id) === String(emp.id)).map(sh => sh.day))].sort();
    let streak = 1;
    for (let i = 1; i < days.length; i++) {
      const diff = (new Date(days[i] + 'T12:00:00') - new Date(days[i - 1] + 'T12:00:00')) / 86400000;
      if (diff === 1) { streak++; if (streak >= 6) hasStreak = true; } else streak = 1;
    }
  });
  if (hasStreak) { sc -= 15; deductions.push({ text: '6+ day streak detected', pts: -15 }); }

  let hasOvertime = false;
  emps.forEach(emp => {
    const maxH = emp.max_hours_per_week || 40;
    if (toInsert.filter(sh => String(sh.employee_id) === String(emp.id)).reduce((a, sh) => a + calcHours(sh.start_time, sh.end_time), 0) > maxH) hasOvertime = true;
  });
  if (hasOvertime) { sc -= 10; deductions.push({ text: 'Overtime detected', pts: -10 }); }

  let uncovered = 0, totalReq = 0;
  DAYS.forEach((day, di) => {
    (tpl[day] || []).forEach(period => {
      Object.entries(staffLevels).forEach(([key, req]) => {
        if (key.startsWith(`${day}-${period}-`) && req > 0) {
          totalReq += req;
          const role = key.split('-').slice(2).join('-');
          const filled = toInsert.filter(sh => sh.day === formatDate(weekDates[di]) && sh.shift_name === period && sh.role === role).length;
          if (filled < req) uncovered += (req - filled);
        }
      });
    });
  });
  if (totalReq > 0 && uncovered > 0) {
    const ded = Math.min(20, Math.round((uncovered / totalReq) * 20));
    if (ded > 0) { sc -= ded; deductions.push({ text: `${uncovered} understaffed slot(s)`, pts: -ded }); }
  }

  const employeesScheduled = new Set(toInsert.map(s => String(s.employee_id))).size;

  return {
    shifts: toInsert,
    score: Math.max(0, sc),
    deductions,
    shiftsFilled: toInsert.length,
    shiftsTotal: totalReq,
    employeesScheduled,
    unfilledSlots,
    conflicts: 0,
  };
}
